import { useCallback, useRef } from "react";
import { toast } from "react-toastify";
import axios from "axios";

const MAX_AGE_TO_CHECK_MS = 5 * 60 * 1000; // 5 phút - chỉ check request có last_status_check > 5 phút

/**
 * Hook để check status của các task đang processing một lần khi load histories
 * @param {string} userId - User ID
 * @returns {Function} Function để check processing tasks
 */
export const useCheckProcessingTasks = (userId) => {
  const hasCheckedRef = useRef(false);
  const checkingRef = useRef(false);

  const checkProcessingTasks = useCallback(
    async (histories) => {
      // Chỉ check một lần
      if (hasCheckedRef.current || checkingRef.current) {
        return;
      }

      if (!userId || !histories || histories.length === 0) {
        return;
      }

      // Lọc các request đang processing và có manus_task_id
      const now = Date.now();
      const processingTasks = histories.filter((history) => {
        const status = history.status?.toLowerCase();
        const isProcessing = [
          "processing",
          "pending",
          "doing",
          "in_progress",
          "not_completed",
          "crawling",
          "uploading",
        ].includes(status);

        // Chỉ check nếu có manus_task_id
        const hasManusTaskId = !!history.manus_task_id;

        // Chỉ check nếu chưa có last_status_check HOẶC last_status_check > 5 phút
        const lastCheck = history.last_status_check
          ? new Date(history.last_status_check).getTime()
          : 0;
        const shouldCheck = !lastCheck || now - lastCheck > MAX_AGE_TO_CHECK_MS;

        return isProcessing && hasManusTaskId && shouldCheck;
      });

      if (processingTasks.length === 0) {
        console.log("✅ [Check Status] No processing tasks to check");
        hasCheckedRef.current = true;
        return;
      }

      console.log(
        `🔍 [Check Status] Checking ${processingTasks.length} processing tasks`
      );

      checkingRef.current = true;

      try {
        // Chuẩn bị data để gửi lên BE
        const tasksToCheck = processingTasks.map((history) => ({
          taskId: history.manus_task_id,
          keyId: history.manus_key_id || null,
          firebaseTaskId: history.taskId || history.request_id,
        }));

        // Call BE endpoint
        const response = await axios.post("/api/manus/tasks/batch-check", {
          tasks: tasksToCheck,
          userId: userId,
        });

        if (response.data && response.data.success) {
          const results = response.data.results || [];
          const updatedCount = results.filter(
            (r) => r.success && r.status !== "already_checking"
          ).length;
          const alreadyCheckingCount = results.filter(
            (r) => r.status === "already_checking"
          ).length;

          console.log(
            `✅ [Check Status] Updated ${updatedCount}/${results.length} tasks${
              alreadyCheckingCount > 0
                ? `, ${alreadyCheckingCount} already being checked`
                : ""
            }`
          );

          // Hiển thị toast nhẹ nếu có task được update
          if (updatedCount > 0) {
            toast.info(`Đã cập nhật trạng thái ${updatedCount} yêu cầu`, {
              position: "top-right",
              autoClose: 3000,
            });
          }
        } else {
          throw new Error(response.data?.error || "Failed to check tasks");
        }
      } catch (error) {
        console.error("❌ [Check Status] Error:", error);
        // Không hiển thị error toast để tránh spam user
        // Chỉ log để debug
      } finally {
        checkingRef.current = false;
        hasCheckedRef.current = true;
      }
    },
    [userId]
  );

  // Reset flag khi userId thay đổi
  const resetCheck = useCallback(() => {
    hasCheckedRef.current = false;
    checkingRef.current = false;
  }, []);

  return { checkProcessingTasks, resetCheck };
};

